import { canSeeBuyFromSalesPartnerButton } from "~/feature/SalesPartnerStockIntegration/salesPartnerStockIntegrationHelper";
import { PDP_GA_NAME } from "~/Search/useSearchPageType";
import { BuyFromSalesPartnerButton } from "~/feature/SalesPartnerStockIntegration/components/BuyFromSalesPartnerButton";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import { Button, type ButtonProps } from "~/common/components/Button/Button";
import { canSeeAddToCartButton, canSeeWishlistButton } from "~/utils/features";
import { DefaultButtonIcon } from "~/common/components/icon/Icon";
import { isProductPMA } from "~/feature/ProductDescriptor/v1/productDescriptorUtils";
import { IconType } from "~/common/components/icon/IconType";
import { Trans } from "react-i18next";
import { userIsCustomer, userIsSalesman } from "~/utils/roles";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import { trackConfigureAndQuoteClick, trackRequestAndQuoteClick } from "~/feature/Analytics/ga/ga";
import { EwellixRequestAQuoteButton } from "~/common/components/Button/PreconfiguredButtons/EwellixRequestAQuoteButton";

type ThemeProps = Pick<ButtonProps, "color" | "variant">;
type ExternalButtonProps = Pick<ButtonProps, "onClick" | "disabled"> & {
  forceChangeToQuote?: boolean;
  quantity?: number;
};

const EWELLIX_BUTTONS = [
  {
    visible: (product: ProductDescriptor) => {
      return !!product.base.lmbtConfigurator;
    },
    propsKey: "configurator",
    render: (product: ProductDescriptor, theme: ThemeProps, externalButtonProps: ExternalButtonProps) => (
      <Button
        key={"configurator"}
        size={"large"}
        startIcon={<BuildOutlinedIcon />}
        fullWidth={true}
        data-test-id={"configurator"}
        {...theme}
        {...externalButtonProps}
        onClick={(event) => {
          trackConfigureAndQuoteClick();
          externalButtonProps.onClick?.(event);
        }}
      >
        <Trans i18nKey={"page.cart.button.configureAndQuote"} />
      </Button>
    ),
  },
  {
    visible: (product: ProductDescriptor) => {
      return true;
    },
    propsKey: "requestAQuote",
    render: (product: ProductDescriptor, theme: ThemeProps, externalButtonProps: ExternalButtonProps) => (
      <EwellixRequestAQuoteButton
        key={"requestAQuote"}
        size={"large"}
        product={product.base}
        onClick={trackRequestAndQuoteClick}
        {...theme}
        {...externalButtonProps}
      />
    ),
  },
];
const SCHAEFFLER_BUTTONS = [
  {
    visible: (product: ProductDescriptor) => canSeeBuyFromSalesPartnerButton(),
    propsKey: "atcFromSP",
    render: renderBuyFromSalesPartnerButton,
  },
  {
    visible: (product: ProductDescriptor) => isProductPMA(product) && productIsLinear(product),
    propsKey: "configurator",
    render: renderConfiguratorButton,
  },
  {
    visible: (product: ProductDescriptor) => canSeeAddToCartButton() && (userIsCustomer() || !productIsLinear(product)),
    propsKey: "atc",
    render: renderATCButton,
  },
  {
    visible: (product: ProductDescriptor) => !isProductPMA(product) && productIsLinear(product),
    propsKey: "configurator",
    render: renderConfiguratorButton,
  },
  {
    visible: (product: ProductDescriptor) => canSeeWishlistButton(),
    propsKey: "wishlist",
    render: renderWishlistButton,
  },
];

type PropsForButtons = {
  atc?: ExternalButtonProps;
  atcFromSP?: ExternalButtonProps & { quantity?: number };
  wishlist?: ExternalButtonProps;
  configurator?: ExternalButtonProps;
};

function getButtonTheme(index: number): ThemeProps {
  if (index === 0) {
    return { color: "primary" };
  } else if (index === 1) {
    return { color: "secondary" };
  } else {
    return { variant: "text" };
  }
}

export function renderButtons(product: ProductDescriptor, externalProps?: PropsForButtons) {
  const buttons = product.base.externalBrand ? EWELLIX_BUTTONS : SCHAEFFLER_BUTTONS;
  return buttons
    .filter(({ visible }) => visible(product))
    .map(({ render, propsKey }, index) => {
      const props: ExternalButtonProps =
        (propsKey ? externalProps?.[propsKey as keyof PropsForButtons] : undefined) ?? {};
      return render(product, getButtonTheme(index), props);
    });
}

function renderATCButton(
  product: ProductDescriptor,
  theme: ThemeProps,
  { forceChangeToQuote, ...externalButtonProps }: ExternalButtonProps,
) {
  let cartButtonLabel;
  let cartButtonIcon;
  let testId;
  if (userIsSalesman()) {
    testId = "pdp-add-to-quote";
    cartButtonLabel = "basket.addToQuote";
    cartButtonIcon = IconType.FileList;
  } else if ((userIsCustomer() && isProductPMA(product)) || forceChangeToQuote) {
    testId = "pdp-add-to-cart";
    cartButtonLabel = "cartPage.btn.quote";
    cartButtonIcon = IconType.FileList;
  } else {
    testId = "pdp-add-to-cart";
    cartButtonLabel = "addToCartPopup.add.submitBtn";
    cartButtonIcon = IconType.Cart;
  }

  return (
    <Button
      key={"buy"}
      size={"large"}
      startIcon={<DefaultButtonIcon type={cartButtonIcon} />}
      fullWidth={true}
      data-test-id={testId}
      {...theme}
      {...externalButtonProps}
    >
      <Trans i18nKey={cartButtonLabel} />
    </Button>
  );
}

function renderConfiguratorButton(
  product: ProductDescriptor,
  theme: ThemeProps,
  externalButtonProps: ExternalButtonProps,
) {
  const url = product.base.configuratorUrl;
  return (
    <Button
      key={"configurator"}
      component={"a"}
      size={"large"}
      startIcon={<BuildOutlinedIcon />}
      fullWidth={true}
      data-test-id={"configurator"}
      {...theme}
      href={url}
      target={"_blank"}
      onClick={(event) => {
        // @ts-expect-error - html element mismatch - event is for Anchor, types are for Button
        externalButtonProps.onClick?.(event);
        trackConfigureAndQuoteClick();
      }}
    >
      <Trans i18nKey={"page.cart.button.configureNow"} />
    </Button>
  );
}

function renderWishlistButton(product: ProductDescriptor, theme: ThemeProps, externalButtonProps: ExternalButtonProps) {
  return (
    <Button
      key={"wishlist"}
      size={"large"}
      startIcon={<DefaultButtonIcon type={IconType.Bookmark} />}
      fullWidth={true}
      data-test-id={"pdp-add-to-wishlist"}
      {...theme}
      {...externalButtonProps}
    >
      <Trans i18nKey={"basket.addToWishlist"} />
    </Button>
  );
}

function renderBuyFromSalesPartnerButton(
  product: ProductDescriptor,
  theme: ThemeProps,
  { quantity }: ExternalButtonProps,
) {
  return (
    <BuyFromSalesPartnerButton
      key={"buy-from-sales-partner"}
      size={"large"}
      fullWidth={true}
      data-test-id={"pdp-buy-from-sales-partner"}
      product={product.base}
      pageType={PDP_GA_NAME}
      quantity={quantity}
      {...theme}
    />
  );
}

function productIsLinear(product: ProductDescriptor) {
  return !!product.base.configuratorUrl;
}
